import React, { useState } from 'react';
import { useDevisRequests } from '../../hooks/useDevisRequests';
import { DemandeDevisRequest } from '../../types/api';

interface DevisRequestFormProps {
  onSubmit: (data: DemandeDevisRequest) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const DevisRequestForm: React.FC<DevisRequestFormProps> = ({
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const { availableTypes, loading: typesLoading } = useDevisRequests();
  
  const [formData, setFormData] = useState<DemandeDevisRequest>({
    batiment_configuration_id: 0,
    message: ''
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'batiment_configuration_id' ? parseInt(value) : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.batiment_configuration_id || formData.batiment_configuration_id === 0) {
      newErrors.batiment_configuration_id = 'Veuillez sélectionner un type de maison';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  if (typesLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="batiment_configuration_id" className="block text-sm font-medium text-gray-700 mb-2">
          Type de maison souhaité
        </label>
        <select
          id="batiment_configuration_id"
          name="batiment_configuration_id"
          value={formData.batiment_configuration_id}
          onChange={handleInputChange}
          className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            errors.batiment_configuration_id ? 'border-red-500' : 'border-gray-300'
          }`}
        >
          <option value={0}>Sélectionnez un type de maison</option>
          {availableTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.type_maison_designation} - {type.batiment_nom} - {type.type_bien} (Niveau {type.niveau}) - {type.projet_nom}
            </option>
          ))}
        </select>
        {errors.batiment_configuration_id && (
          <p className="mt-1 text-sm text-red-600">{errors.batiment_configuration_id}</p>
        )}
      </div>

      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
          Message (optionnel)
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleInputChange}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Ajoutez des informations complémentaires à votre demande..."
        />
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Envoi en cours...' : 'Envoyer la demande'}
        </button>
      </div>
    </form>
  );
};
